import React, { useState, useCallback } from 'react';
import { useNavigate, Link } from '@tanstack/react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

import { AuthLayout } from '@/components/auth/AuthLayout';
import { AuthThemeToggle } from '@/components/auth/ThemeToggle';
import { authThemeConfigs, animationVariants } from '@/components/auth/constants';
import {
  PersonalInfoStep,
  AccountSetupStep,
  SuccessStep,
  initialFormData,
  stepOrder,
  stepConfig,
  getNextStep,
  getPreviousStep,
  validatePersonalInfo,
  validateAccountSetup,
} from '@/components/auth/register';
import { useAuth } from '../contexts/AuthContext';
import { useAuthTheme } from '../contexts/AuthThemeContext';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { authTheme, setAuthTheme } = useAuthTheme();

  // Get theme configuration
  const themeConfig = authThemeConfigs[authTheme] || authThemeConfigs.invol;

  const [step, setStep] = useState('personal_info');
  const [direction, setDirection] = useState('forward');
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get current step index for progress dots
  const currentStepIndex = stepConfig[step]?.index ?? 0;
  const totalSteps = stepOrder.length - 1; // Exclude success step from count

  // Navigate to next step
  const goToStep = useCallback((newStep, dir = 'forward') => {
    setDirection(dir);
    setErrors({});
    setStep(newStep);
  }, []);

  // Handle form field changes
  const handleChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear field error when user types
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [errors]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    const prevStep = getPreviousStep(step);
    if (prevStep) {
      goToStep(prevStep, 'backward');
    }
  }, [step, goToStep]);

  // Handle next navigation with validation
  const handleNext = useCallback(() => {
    let validationErrors = {};

    // Validate current step
    if (step === 'personal_info') {
      validationErrors = validatePersonalInfo(formData);
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const nextStep = getNextStep(step);
    if (nextStep) {
      goToStep(nextStep, 'forward');
    }
  }, [step, formData, goToStep]);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    // Validate account setup
    const validationErrors = validateAccountSetup(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const result = await register({
        username: formData.username,
        email: formData.email,
        password: formData.password,
        password_confirm: formData.password_confirm,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone_number: formData.phone_number,
        user_type: formData.user_type,
      });

      if (result.success) {
        goToStep('success', 'forward');

        // Auto-logged in, redirect to dashboard
        setTimeout(() => {
          navigate({ to: '/client' });
        }, 1500);
      } else {
        setErrors({ submit: result.error });
      }
    } catch (err) {
      setErrors({ submit: 'An unexpected error occurred. Please try again.' });
      console.error('Registration error:', err);
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, register, navigate, goToStep]);

  // Handle continue button - go to dashboard
  const handleContinue = useCallback(() => {
    navigate({ to: '/client' });
  }, [navigate]);

  // Get animation variants based on direction
  const getVariants = () => {
    return direction === 'forward'
      ? animationVariants.stepForward
      : animationVariants.stepBackward;
  };

  return (
    <AuthLayout variant="register" theme={authTheme}>
      <div className="w-full max-w-lg mx-auto">
        {/* Progress dots (hide on success) */}
        {step !== 'success' && (
          <motion.div
            className="flex justify-center gap-2 mb-8"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            {Array.from({ length: totalSteps }).map((_, i) => (
              <motion.div
                key={i}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === currentStepIndex
                    ? `w-8 bg-gradient-to-r ${themeConfig.progressActive}`
                    : i < currentStepIndex
                    ? `w-2 ${themeConfig.progressCompleted}`
                    : 'w-2 bg-slate-200'
                }`}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1 + i * 0.05 }}
              />
            ))}
          </motion.div>
        )}

        {/* Glassmorphism card with gradient border */}
        <div className={`relative p-[1px] rounded-2xl bg-gradient-to-r ${themeConfig.cardBorder}`}>
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl p-8">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={step}
                variants={getVariants()}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                {step === 'personal_info' && (
                  <PersonalInfoStep
                    formData={formData}
                    errors={errors}
                    onChange={handleChange}
                    onBack={handleBack}
                    onNext={handleNext}
                    theme={authTheme}
                  />
                )}

                {step === 'account_setup' && (
                  <AccountSetupStep
                    formData={formData}
                    errors={errors}
                    isSubmitting={isSubmitting}
                    onChange={handleChange}
                    onBack={handleBack}
                    onSubmit={handleSubmit}
                    theme={authTheme}
                  />
                )}

                {step === 'success' && (
                  <SuccessStep onContinue={handleContinue} theme={authTheme} />
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Login link (hide on success) */}
        {step !== 'success' && (
          <motion.div
            className="mt-8 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <p className="text-sm text-slate-500">
              Already have an account?{' '}
              <Link
                to="/login"
                className={`font-medium ${themeConfig.link} ${themeConfig.linkHover} relative group inline-flex items-center gap-1`}
              >
                Sign in
                <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                <span className={`absolute bottom-0 left-0 w-0 h-0.5 ${themeConfig.linkUnderline} group-hover:w-full transition-all duration-300`} />
              </Link>
            </p>
          </motion.div>
        )}
      </div>

      {/* Theme toggle */}
      <AuthThemeToggle currentTheme={authTheme} onToggle={setAuthTheme} />
    </AuthLayout>
  );
};

export default RegisterPage;
